/**
 * The opportunity workspace — everything known about one structure, on one screen.
 *
 * ── What this screen is for ──────────────────────────────────────────────────
 *
 * It is the screen somebody stands in a yard holding, with a seller waiting.
 * So the order is not "most data first" — it is: what do I do next, is it
 * worth it, what does it really cost, who do I call, and only then the
 * evidence. The NEXT BEST ACTION is above everything and is never folded.
 *
 * ── Eleven requests, and none of them can take the screen down ───────────────
 *
 * The composition needs the opportunity, the score, the cost, the risk, the
 * action, the checklist, the transport answer, the documents, the seller and
 * the price history. That is a lot of round trips on a phone with two bars, so:
 *
 *  - the opportunity itself loads FIRST and renders on its own. Everything
 *    else is a panel, and a panel that failed says so in its own body while
 *    the rest of the screen works.
 *  - it goes through `oppCache.readThrough`, so a failed fetch in the field
 *    renders the last payload behind a visible "showing data from 09:14"
 *    banner. §J's first requirement. A stale asking price is exactly the
 *    number somebody is about to say out loud, so the banner is not optional
 *    and the cache is never presented as live.
 *
 * ── The land banner is persistent, and it is not a nag ───────────────────────
 *
 * While the parcel's measurements are NULL, `shared/access.ts` emits a caution
 * for every rule it could not run, the roll-up is grey NOT FULLY VERIFIED, and
 * the honest description of that state is that access warnings are disabled —
 * because they are: no rule can fire against a NULL. The banner says so on
 * every opportunity until somebody walks the land with a tape measure, and it
 * links straight to the form that fixes it.
 *
 * ── §10.3, §J ────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useState } from 'react'
import { get } from '../lib/api'
import { useLocale } from '../lib/i18n'
import type { Locale } from '../lib/i18n'
import { Link, useRoute } from '../lib/route'
import * as oppCache from '../lib/oppCache'
import { useFeatures } from '../lib/features'
import Icon from '../components/Icon'
import {
  accessLabel,
  accessTone,
  ago,
  assumed,
  costBandTone,
  money,
  range,
  score as scoreText,
  scoreTone,
  timeText,
  TONES,
} from '../lib/format'
import {
  availabilityLabel,
  conditionLabel,
  dispositionLabel,
  laneLabel,
  measurementLabel,
  reasonLabel,
  stageLabel,
  structureLabel,
  titleStatusLabel,
} from '../lib/domain'
import { Banner, Chip, Empty, Line, Panel, S } from '../components/detail/ui'
import NextAction from '../components/detail/NextAction'
import ScoreCard from '../components/detail/ScoreCard'
import CostBreakdown from '../components/detail/CostBreakdown'
import RiskPanel from '../components/detail/RiskPanel'
import FactsPanel from '../components/detail/FactsPanel'
import SellerCard from '../components/detail/SellerCard'
import DiligenceList from '../components/detail/DiligenceList'
import TransportPanel, { missingLandMeasurements } from '../components/detail/TransportPanel'
import DocumentsPanel from '../components/detail/DocumentsPanel'
import PriceTimeline from '../components/detail/PriceTimeline'
import type {
  ActionView,
  CostSummary,
  DiligenceView,
  DocumentRow,
  ListingDetail,
  ListingRow,
  OpportunityPayload,
  PricePoint,
  RiskView,
  ScoreView,
  SellerPayload,
  TransportPayload,
} from '../components/detail/types'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  loading: 'Loading…',
  failed: 'This could not be loaded.',
  failedWhy: 'It may have been merged into another opportunity, or you may be offline with nothing cached.',
  back: 'Back to the hunt',
  fromCache: 'Showing data from {time} — this device is offline.',
  landIncomplete: 'Land profile incomplete — access warnings disabled.',
  landIncompleteWhy:
    'No access rule can fire against a measurement nobody has taken, so nothing here can tell you whether this fits. Measure {list} and it turns into a real answer.',
  landFix: 'Set up the land profile',
  mayBeGone: 'Every listing for this one has gone quiet. It may already be sold.',
  asking: 'Asking',
  landed: 'Landed cost',
  noPrice: 'No asking price recorded',
  noPriceWhy:
    'Without it the landed cost cannot be built, the score loses its heaviest factor, and this cannot qualify for an instant alert.',
  inspect: 'Start an inspection',
  openListing: 'Open the listing',
  sources: 'Listings',
  noSources: 'No listing is attached to this one.',
  notes: 'Notes',
  noNotes: 'No notes yet.',
  history: 'Decisions',
  noHistory: 'Nothing has been decided yet.',
  pinned: 'Pinned',
  finalist: 'Finalist',
  favourite: 'Favourite',
  watched: 'Watching the price',
  dormant: 'Dormant',
  lastActivity: 'Last activity',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  loading: 'Cargando…',
  failed: 'Esto no se pudo cargar.',
  failedWhy: 'Puede que se haya combinado con otra oportunidad, o que estés sin conexión y sin nada guardado.',
  back: 'Volver a la búsqueda',
  fromCache: 'Mostrando datos de las {time} — este equipo está sin conexión.',
  landIncomplete: 'El perfil del terreno está incompleto — los avisos de acceso están apagados.',
  landIncompleteWhy:
    'Ninguna regla de acceso puede correr sobre una medida que nadie ha tomado, así que nada aquí te puede decir si esto cabe. Mide {list} y se convierte en una respuesta de verdad.',
  landFix: 'Configurar el terreno',
  mayBeGone: 'Todos los anuncios de esta se quedaron callados. Puede que ya se haya vendido.',
  asking: 'Piden',
  landed: 'Costo puesto',
  noPrice: 'No hay precio pedido anotado',
  noPriceWhy:
    'Sin él no se puede armar el costo puesto, la puntuación pierde su factor más pesado, y esto no puede calificar para una alerta inmediata.',
  inspect: 'Empezar una inspección',
  openListing: 'Abrir el anuncio',
  sources: 'Anuncios',
  noSources: 'No hay ningún anuncio ligado a esto.',
  notes: 'Notas',
  noNotes: 'Todavía no hay notas.',
  history: 'Decisiones',
  noHistory: 'Todavía no se ha decidido nada.',
  pinned: 'Fijada',
  finalist: 'Finalista',
  favourite: 'Favorita',
  watched: 'Vigilando el precio',
  dormant: 'Inactiva',
  lastActivity: 'Última actividad',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/* ────────────────────────────────────────────────────────────────────────────
 * The panels' own load state
 *
 * One flag per panel rather than one for the screen: a failed cost fetch must
 * not blank the score, and "we could not load this" in one panel body is a far
 * better answer than an empty screen with no explanation.
 * ────────────────────────────────────────────────────────────────────────── */

interface Panels {
  score: ScoreView | null
  cost: CostSummary | null
  risk: RiskView | null
  action: ActionView | null
  diligence: DiligenceView | null
  transport: TransportPayload | null
  documents: DocumentRow[]
  seller: SellerPayload | null
  priceHistory: PricePoint[]
}

const NO_PANELS: Panels = {
  score: null,
  cost: null,
  risk: null,
  action: null,
  diligence: null,
  transport: null,
  documents: [],
  seller: null,
  priceHistory: [],
}

type PanelName = keyof Panels

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function OpportunityDetail({ id: propId }: { id?: string } = {}) {
  const locale = useLocale()
  const on = useFeatures()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])
  const route = useRoute()
  const id = propId ?? route.params.id ?? ''

  const [payload, setPayload] = useState<OpportunityPayload | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [fromCache, setFromCache] = useState(false)
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [panels, setPanels] = useState<Panels>(NO_PANELS)
  const [failedPanels, setFailedPanels] = useState<Set<PanelName>>(new Set())

  /* The core payload. Cached, because this is the screen used with no signal. */
  useEffect(() => {
    if (!id) {
      setState('failed')
      return
    }
    let cancelled = false
    setState('loading')

    void (async () => {
      try {
        const result = await oppCache.readThrough<OpportunityPayload>(id, () =>
          get<OpportunityPayload>(`/api/opportunities/${encodeURIComponent(id)}`),
        )
        if (cancelled) return
        setPayload(result.payload)
        setFromCache(result.fromCache)
        setSavedAt(result.savedAt)
        setState('ready')
      } catch {
        // No network AND no cache. The real error, not an empty state that
        // reads as "this opportunity has no data".
        if (!cancelled) setState('failed')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [id])

  /* Everything else, in parallel, each failing on its own. */
  useEffect(() => {
    if (!id || state !== 'ready') return
    let cancelled = false

    /**
     * One panel's fetch. A failure marks THAT panel and returns null; it never
     * rejects, so `Promise.all` below cannot lose the eight that succeeded to
     * the one that did not.
     */
    const load = async <T,>(name: PanelName, path: string, pick: (body: unknown) => T): Promise<T | null> => {
      try {
        return pick(await get<unknown>(path))
      } catch {
        if (!cancelled) setFailedPanels((current) => new Set(current).add(name))
        return null
      }
    }

    const oppPath = `/api/opportunities/${encodeURIComponent(id)}`
    const contactId = payload?.opportunity.primary_contact_id ?? null
    const primaryListing: ListingRow | undefined = payload?.listings[0]

    void (async () => {
      const [score, cost, risk, action, diligence, transport, documents, seller, listing] = await Promise.all([
        load<ScoreView>('score', `${oppPath}/score`, (b) => (b as { score: ScoreView }).score),
        load<CostSummary>('cost', `${oppPath}/cost`, (b) => (b as { cost: CostSummary }).cost),
        load<RiskView>('risk', `${oppPath}/risk`, (b) => (b as { risk: RiskView }).risk),
        load<ActionView>('action', `${oppPath}/action`, (b) => (b as { action: ActionView }).action),
        load<DiligenceView>('diligence', `${oppPath}/diligence`, (b) => b as DiligenceView),
        load<TransportPayload>('transport', `${oppPath}/transport`, (b) => b as TransportPayload),
        load<DocumentRow[]>(
          'documents',
          `/api/documents?opportunityId=${encodeURIComponent(id)}`,
          (b) => (b as { documents: DocumentRow[] }).documents,
        ),
        contactId
          ? load<SellerPayload>('seller', `/api/sellers/${encodeURIComponent(contactId)}`, (b) => b as SellerPayload)
          : Promise.resolve(null),
        primaryListing
          ? load<ListingDetail>(
              'priceHistory',
              `/api/listings/${encodeURIComponent(primaryListing.id)}`,
              (b) => b as ListingDetail,
            )
          : Promise.resolve(null),
      ])

      if (cancelled) return
      setPanels({
        score,
        cost,
        risk,
        action,
        diligence,
        transport,
        documents: documents ?? [],
        seller,
        priceHistory: listing?.priceHistory ?? [],
      })
    })()

    return () => {
      cancelled = true
    }
  }, [id, state, payload])

  if (state === 'loading') return <div style={S.page}>{t('loading')}</div>

  if (state === 'failed' || !payload) {
    return (
      <div style={S.page}>
        <h1 style={{ fontSize: 20, fontWeight: 800 }}>{t('failed')}</h1>
        <p style={S.note}>{t('failedWhy')}</p>
        <Link to="/feed" style={{ ...S.button, marginTop: 12, textDecoration: 'none' }}>
          <Icon name="home" size={18} />
          {t('back')}
        </Link>
      </div>
    )
  }

  const { opportunity, current, listings, facts, unknownFacts, notes, decisions } = payload
  const askingCents = current?.ask_price_cents ?? listings[0]?.price_cents ?? null
  const landed =
    current && current.landed_expected_cents !== null
      ? {
          low: current.landed_low_cents ?? current.landed_expected_cents,
          expected: current.landed_expected_cents,
          high: current.landed_high_cents ?? current.landed_expected_cents,
        }
      : panels.cost?.total ?? null

  const missingLand = missingLandMeasurements(panels.transport?.access ?? null)
  const accessRollUp = panels.transport?.access?.rollUp ?? null

  return (
    <div style={S.page}>
      {/* ── Banners ─────────────────────────────────────────────────────── */}

      {fromCache && (
        <Banner tone={TONES.warn} icon="clock">
          {t('fromCache').replace('{time}', timeText(savedAt, locale))}
        </Banner>
      )}

      {payload.mayBeGone && (
        <Banner tone={TONES.warn} icon="alert">
          {t('mayBeGone')}
        </Banner>
      )}

      {/* Persistent, and it stays until somebody measures the parcel. */}
      {missingLand.length > 0 && (
        <Banner tone={TONES.quiet} icon="alert" outlined>
          <strong>{t('landIncomplete')}</strong>{' '}
          {t('landIncompleteWhy').replace('{list}', missingLand.map((key) => measurementLabel(key, locale)).join(', '))}{' '}
          <Link to="/setup/land" style={{ color: 'inherit', fontWeight: 700 }}>
            {t('landFix')}
          </Link>
        </Banner>
      )}

      {/* ── The head ────────────────────────────────────────────────────── */}

      <header style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, letterSpacing: '0.05em' }}>
          {opportunity.ref ?? opportunity.id}
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '2px 0 8px', lineHeight: 1.25 }}>
          {opportunity.title ?? listings[0]?.title ?? opportunity.id}
        </h1>

        <div style={S.chipRow}>
          <Chip tone={TONES.brand}>{stageLabel(opportunity.stage, locale)}</Chip>
          {payload.lane && <Chip tone={TONES.quiet} outlined>{laneLabel(payload.lane, locale)}</Chip>}
          {opportunity.disposition !== 'open' && (
            <Chip tone={TONES.quiet}>{dispositionLabel(opportunity.disposition, locale)}</Chip>
          )}
          {opportunity.is_finalist === 1 && <Chip tone={TONES.strong} icon="star">{t('finalist')}</Chip>}
          {opportunity.is_favorite === 1 && <Chip tone={TONES.quiet} icon="heart" outlined>{t('favourite')}</Chip>}
          {opportunity.is_watched === 1 && <Chip tone={TONES.quiet} icon="eye" outlined>{t('watched')}</Chip>}
          {payload.dormancy?.dormant && <Chip tone={TONES.warn}>{t('dormant')}</Chip>}
          {panels.score?.score !== null && panels.score !== null && (
            <Chip tone={scoreTone(panels.score.score)}>{scoreText(panels.score.score)}</Chip>
          )}
          {accessRollUp && <Chip tone={accessTone(accessRollUp.level)}>{accessLabel(accessRollUp.level, locale)}</Chip>}
        </div>

        {/* Price, at the size the brief asked for: obvious before it is read. */}
        <div style={{ marginTop: 12 }}>
          {askingCents === null ? (
            <>
              <div style={{ ...S.big, color: 'var(--warn)' }}>{t('noPrice')}</div>
              <p style={S.note}>{t('noPriceWhy')}</p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('asking')}</div>
              <div style={{ ...S.huge }}>{money(askingCents, locale)}</div>
            </>
          )}

          {landed && (
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'var(--money-bg)',
                  color: 'var(--money)',
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {t('landed')} {range(landed, locale)}
              </span>
              {panels.cost && (
                <Chip tone={costBandTone(panels.cost.confidence.band)}>{panels.cost.confidence.band}</Chip>
              )}
              {panels.cost?.lines.some((line) => line.basis === 'assumption') && (
                <Chip tone={TONES.quiet} outlined>
                  {assumed(locale)}
                </Chip>
              )}
            </div>
          )}
        </div>

        <div style={{ ...S.chipRow, marginTop: 10 }}>
          {opportunity.structure_type && (
            <Chip tone={TONES.quiet} outlined>{structureLabel(opportunity.structure_type, locale)}</Chip>
          )}
          {opportunity.year !== null && <Chip tone={TONES.quiet} outlined>{opportunity.year}</Chip>}
          {opportunity.condition && (
            <Chip tone={TONES.quiet} outlined>{conditionLabel(opportunity.condition, locale)}</Chip>
          )}
          {opportunity.title_status && (
            <Chip tone={TONES.quiet} outlined>{titleStatusLabel(opportunity.title_status, locale)}</Chip>
          )}
        </div>

        {on('nav.inspect') && (
          <Link
            to={`/o/${encodeURIComponent(opportunity.id)}/inspect`}
            style={{ ...S.button, width: '100%', marginTop: 12, textDecoration: 'none' }}
          >
            <Icon name="camera" size={18} />
            {t('inspect')}
          </Link>
        )}
      </header>

      {/* ── The one thing to do ─────────────────────────────────────────── */}

      {on('detail.nextAction') && <NextAction action={panels.action} failed={failedPanels.has('action')} />}

      {/* ── The panels ──────────────────────────────────────────────────────
          Each one is a switch on the settings page, and every one of them
          starts OFF. Ten stacked panels is the whole reason this screen became
          unreadable: the page is only as long as the owner asked for. The
          fetches above are unconditional on purpose — a panel switched on
          later must not need a reload to have its data, and the requests are
          already parallel and already cached. */}

      {on('detail.score') && <ScoreCard score={panels.score} failed={failedPanels.has('score')} />}
      {on('detail.cost') && <CostBreakdown cost={panels.cost} failed={failedPanels.has('cost')} />}
      {on('detail.risk') && <RiskPanel risk={panels.risk} failed={failedPanels.has('risk')} />}
      {on('detail.seller') && <SellerCard seller={panels.seller} failed={failedPanels.has('seller')} />}
      {on('detail.diligence') && <DiligenceList diligence={panels.diligence} failed={failedPanels.has('diligence')} />}
      {on('detail.transport') && <TransportPanel transport={panels.transport} failed={failedPanels.has('transport')} />}
      {on('detail.facts') && <FactsPanel facts={facts} unknown={unknownFacts} />}
      {on('detail.priceHistory') && <PriceTimeline points={panels.priceHistory} failed={failedPanels.has('priceHistory')} />}
      {on('detail.documents') && <DocumentsPanel documents={panels.documents} failed={failedPanels.has('documents')} />}

      <Panel id="sources" title={t('sources')} badge={<Chip tone={TONES.quiet}>{listings.length}</Chip>}>
        {listings.length === 0 ? (
          <Empty>{t('noSources')}</Empty>
        ) : (
          listings.map((listing, index) => (
            <div key={listing.id} style={{ padding: '9px 0', borderTop: index === 0 ? 'none' : '1px solid var(--line)' }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{listing.title ?? listing.id}</div>
              <div style={{ ...S.chipRow, marginTop: 6 }}>
                <Chip tone={listing.availability === 'active' ? TONES.strong : TONES.quiet} outlined={listing.availability !== 'active'}>
                  {availabilityLabel(listing.availability, locale)}
                </Chip>
                {listing.price_cents !== null && <Chip tone={TONES.money}>{money(listing.price_cents, locale)}</Chip>}
                {listing.last_seen_at !== null && (
                  <Chip tone={TONES.quiet} outlined>
                    {ago(listing.last_seen_at, locale)}
                  </Chip>
                )}
              </div>
              {listing.location_text && <p style={S.note}>{listing.location_text}</p>}
              {listing.source_url && (
                <a
                  href={listing.source_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...S.button, marginTop: 8, textDecoration: 'none' }}
                >
                  {t('openListing')}
                </a>
              )}
            </div>
          ))
        )}
      </Panel>

      <Panel id="notes" title={t('notes')} badge={<Chip tone={TONES.quiet}>{notes.length}</Chip>}>
        {notes.length === 0 ? (
          <Empty>{t('noNotes')}</Empty>
        ) : (
          notes.map((note, index) => (
            <div key={note.id} style={{ padding: '9px 0', borderTop: index === 0 ? 'none' : '1px solid var(--line)' }}>
              {note.pinned === 1 && (
                <span style={{ ...S.chipRow, marginBottom: 4 }}>
                  <Chip tone={TONES.brand}>{t('pinned')}</Chip>
                </span>
              )}
              <p style={{ ...S.sentence, margin: 0, whiteSpace: 'pre-wrap' }}>{note.body}</p>
              <p style={S.note}>{ago(note.created_at, locale)}</p>
            </div>
          ))
        )}
      </Panel>

      <Panel id="history" title={t('history')} badge={<Chip tone={TONES.quiet}>{decisions.length}</Chip>}>
        {decisions.length === 0 ? (
          <Empty>{t('noHistory')}</Empty>
        ) : (
          decisions.map((decision, index) => (
            <div key={decision.id} style={{ padding: '9px 0', borderTop: index === 0 ? 'none' : '1px solid var(--line)' }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {decision.from_stage ? `${stageLabel(decision.from_stage, locale)} → ` : ''}
                {decision.to_stage
                  ? stageLabel(decision.to_stage, locale)
                  : decision.to_disposition
                    ? dispositionLabel(decision.to_disposition, locale)
                    : decision.kind}
              </div>
              {decision.reason_code && <p style={S.note}>{reasonLabel(decision.reason_code, locale)}</p>}
              {decision.reason && <p style={S.sentence}>{decision.reason}</p>}
              <p style={S.note}>{ago(decision.decided_at, locale)}</p>
            </div>
          ))
        )}
      </Panel>

      <Line label={t('lastActivity')} first>
        {ago(opportunity.last_activity_at, locale)}
      </Line>
    </div>
  )
}
